import { Controller } from "@hotwired/stimulus";
import { t } from "../../lib/i18n";
import {
  totalOutputDuration,
  segmentOutputDuration,
  segmentOutputStart,
  cutAtTime,
  segmentAt,
  isPlayableVideo,
  removeSegment,
  setSegmentSpeed,
  reorderSegments,
  duplicateSegment,
  toggleOverlayVisibility,
  formatTime,
  formatTimeShort,
} from "../../lib/composition_v3";
import {
  capturePositions,
  playFlip,
} from "../../lib/editor_animations";
import Sortable from "sortablejs";
import { ToolboxDropZone } from "../../lib/toolbox_drop_zone";
import {
  computeRuler,
  computeMediaTrack,
  computeLayersTrack,
  countAnimMarks,
  computeAudioTrack,
  startSegmentTrim,
  startOverlayDrag,
  startAudioDrag,
} from "../../lib/editor_tracks.ts";

const TICK_CLASSES = {
  major: "absolute bottom-0 h-2 w-px bg-surface_300",
  minor: "absolute bottom-0 h-1.5 w-px bg-surface_300",
  quarter: "absolute bottom-0 h-[5px] w-px bg-surface_200",
  sub: "absolute bottom-0 h-[3px] w-px bg-surface_100",
};

const LABEL_CLS = "absolute top-0 font-mono text-[0.58rem] text-surface_400 tabular-nums";

const LABEL_ALIGN = { start: "", end: "-translate-x-full", center: "-translate-x-1/2" };

const px = (v) => `${v}px`;

const seconds = (v) => t("composition_editor.layers.seconds", "%{s} s")
  .replace("%{s}", v.toLocaleString(document.documentElement.lang || undefined,
                                    { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

/** L'infobulle d'une marque : le côté, l'effet, et ce que fait le clic. */
function animMarkTip(mark) {
  const side = t(`composition_editor.layers.anim_${mark.side}`, mark.side === "in" ? "Vous avez choisi une animation d'entrée" : "Vous avez choisi une animation de sortie");
  return `${side} · ${mark.label}. ${t("composition_editor.layers.anim_edit", "Cliquez pour la modifier.")}`;
}

/** L'étiquette d'un geste sur une barre de calque : la fenêtre entière, ou le bout tiré. */
function dragLabel(overlay, handle) {
  if (handle === "L") return t("composition_editor.layers.drag_start", "départ %{t}").replace("%{t}", seconds(overlay.start));
  if (handle === "R") return t("composition_editor.layers.drag_end", "fin %{t}").replace("%{t}", seconds(overlay.end));
  return `${seconds(overlay.start)} → ${seconds(overlay.end)}`;
}
const pct = (v) => `${v}%`;

const byRole = (el, role) => el.querySelector(`[data-role="${role}"]`);

/**
 * Manages the timeline tracks area of the composition editor.
 *
 * Handles:
 *  - rendering ruler, media/overlay/audio tracks
 *  - playhead positioning + scrub
 *  - cut at the playhead
 *  - segment click/trim/reorder (SortableJS)
 *  - overlay click/trim/drag + group toggle
 *  - audio click/trim/drag + soundtrack select/remove
 *  - the selected sequence's gestures (speed, repeat, duplicate, delete), called from its page bar
 *
 * Communicates with the main composition-editor controller via outlet.
 * Naming: "composition-editor--tracks" in HTML.
 */
export default class extends Controller {
  static outlets = ["composition-editor"];

  // Les presets d'animation (groupe, nom affiché), pour les marques des barres de calques.
  static values = { presets: { type: Object, default: {} } };

  static targets = [
    "ruler",
    "playhead",
    "trackMedia",
    "trackLayers",
    "trackAudio",
    "layersSummary",
    "tracksArea",
    "tlWrap",
    "timeCur",
    "timeTotal",
    // Templates
    "segmentWrapTpl",
    "segmentTpl",
    "overlayTpl",
    "layersEmptyTpl",
    "audioTpl",
    "audioEmptyTpl",
    "audioCandidatesTpl",
  ];

  static values = {
    // La page Audio de la toolbox du porteur (vide pour l'éditeur legacy d'un ContentMedium).
    audioUrl: String,
  };

  // ==================== Lifecycle ====================

  connect() {
    this._groupOpen = false;
    this._sortable = null;
    this._initialized = false;

    this._tryInit();
  }

  compositionEditorOutletConnected() {
    this._tryInit();
  }

  _tryInit() {
    if (this._initialized || !this._editor || !this.hasTrackMediaTarget) return;
    this._initialized = true;

    // Drop zone for toolbox items onto the timeline
    this._toolboxDrop = new ToolboxDropZone(this.trackMediaTarget, {
      onDragOver: (e) => {
        const { kind, index } = this._dropTarget(e);
        this._showDropIndicator(kind, index);
      },
      onDrop: (e, data) => {
        this._clearDropIndicator();
        const { kind, index } = this._dropTarget(e);
        const cm = { url: data.url, id: data.id, media_type: data.media_type };
        // Une carte dynamique garde son slot, comme au clic.
        const ctx = { index, dynamic: data.dynamic };
        if (kind === "replace") {
          this._editor.replaceSegmentMedia(cm, ctx);
        } else {
          this._editor.insertSegmentFromMedia(cm, ctx);
        }
      },
      onLeave: () => this._clearDropIndicator(),
    });

    this._rulerRO = new ResizeObserver(() => this.renderAll());
    this._rulerRO.observe(this.rulerTarget);

    this.renderAll();
  }

  disconnect() {
    this._rulerRO?.disconnect();
    this._toolboxDrop?.destroy();
    this.hideSpeedPop();
    if (this._sortable) this._sortable.destroy();
  }

  // ==================== Public API (called by parent) ====================

  /** Full re-render of all tracks + ruler + playhead. */
  renderAll() {
    const editor = this._editor;
    if (!editor) return;

    const comp = editor.composition;
    const td = totalOutputDuration(comp);
    const pps = this._pxPerSec();
    const sel = editor.selection;

    this._renderRuler(computeRuler(td));
    this._renderMedia(computeMediaTrack(comp.segments || [], sel, pps));
    this._renderLayersTrack();
    this._renderAudio(computeAudioTrack(comp.audio, sel, td));
    this._setupSortable();
    this.setPlayhead(editor.playTime);
  }

  /** Update playhead position and timecode displays. */
  setPlayhead(t) {
    const editor = this._editor;
    if (!editor) return;

    const composition = editor.composition;
    const td = totalOutputDuration(composition);
    const clamped = Math.max(0, Math.min(t, td || 1));
    editor.setPlayTime(clamped);

    if (!this.hasRulerTarget || td === 0) return;
    const rulerOffset =
      this.rulerTarget.getBoundingClientRect().left -
      this.rulerTarget.closest('[data-composition-editor--tracks-target="tlWrap"]').getBoundingClientRect().left;
    this.playheadTarget.style.left = px(rulerOffset + (clamped / td) * this.rulerTarget.clientWidth);
    const curTxt = formatTime(clamped);
    const totalTxt = formatTimeShort(td);
    this.timeCurTargets.forEach((el) => { el.textContent = curTxt; });
    this.timeTotalTargets.forEach((el) => { el.textContent = totalTxt; });
    editor.updateTimecodes(curTxt, totalTxt);
    editor.fabric?.syncVisibility();
  }

  /** Whether overlay group is expanded. */
  get groupOpen() { return this._groupOpen; }

  /** Set overlay group expanded state (called by fabric controller via parent). */
  setGroupOpen(open) { this._groupOpen = open; }

  /** Ferme les menus flottants ouverts (appelé par le parent : déconnexion, Échap). */
  hideSpeedPop() {
    window.dispatchEvent(new Event("floating-menu:close"));
  }

  /** Delete a segment by index (called by parent for keyboard shortcut). */
  deleteSegment(segIndex) {
    this._deleteSegment(segIndex);
  }

  // ==================== DOM helpers ====================

  _cloneTpl(name) {
    return this[`${name}Target`].content.cloneNode(true).firstElementChild;
  }

  /** Set Stimulus action params on elements that declare them in the template. */
  _setParams(el, params) {
    for (const [key, value] of Object.entries(params)) {
      const attr = `data-composition-editor--tracks-${key}-param`;
      el.querySelectorAll(`[${attr}]`).forEach(h => { h.setAttribute(attr, value); });
    }
  }

  /** Set Stimulus values on an element (uses setAttribute to handle double-dash identifiers). */
  _setValues(el, controller, values) {
    for (const [key, value] of Object.entries(values)) {
      el.setAttribute(`data-${controller}-${key}-value`, value);
    }
  }

  // ==================== DOM rendering (from descriptors) ====================

  _renderRuler(ticks) {
    const ruler = this.rulerTarget;
    if (!ticks.length) { ruler.replaceChildren(); return; }
    let html = "";
    for (const tick of ticks) {
      html += `<span class="${TICK_CLASSES[tick.type]}" style="left:${pct(tick.pct)}"></span>`;
      if (tick.label) {
        html += `<span class="${LABEL_CLS} ${LABEL_ALIGN[tick.align]}" style="left:${pct(tick.pct)}">${tick.label}</span>`;
      }
    }
    ruler.innerHTML = html;
  }

  _renderMedia(descriptors) {
    const track = this.trackMediaTarget;
    Object.assign(track.style, { display: "flex", gap: "0px" });
    if (!descriptors.length) { track.replaceChildren(); return; }

    const frag = document.createDocumentFragment();
    for (const desc of descriptors) {
      frag.appendChild(this._buildSegmentWrap(desc));
    }
    // Trailing insert zone
    const endWrap = this._cloneTpl("segmentWrapTpl");
    endWrap.style.height = "100%";
    endWrap.querySelector("[data-insert-index]").dataset.insertIndex = descriptors.length;
    frag.appendChild(endWrap);
    track.replaceChildren(frag);
  }

  _buildSegmentWrap(desc) {
    const wrap = this._cloneTpl("segmentWrapTpl");
    Object.assign(wrap.style, { width: px(desc.width), flex: "none" });
    wrap.dataset.id = desc.id;
    wrap.querySelector("[data-insert-index]").dataset.insertIndex = desc.index;

    const seg = this._cloneTpl("segmentTpl");
    const values = {
      state: desc.state,
      "seg-index": desc.index,
      title: desc.title || "",
      background: desc.background || "",
    };

    if (desc.state === "ready") {
      values.selected = desc.selected || false;
      values.speed = desc.speed || 0;
      values.repeat = desc.repeat || 0;
      values.slot = desc.slot || "";
      values.icon = desc.icon;
      values.thumbnails = JSON.stringify(desc.thumbnails);
    }

    this._setValues(seg, "composition-editor--tracks--segment", values);
    byRole(wrap, "content").appendChild(seg);
    return wrap;
  }

  /**
   * La piste Calques, recalculée depuis le document : les lignes rangées comme le canvas (le
   * bloc du dessus en haut). Un bloc ajouté à la vidéo déplie la piste et — la première fois
   * — une bulle dit où le retrouver.
   */
  _renderLayersTrack() {
    const editor = this._editor;
    const overlays = editor.composition.overlays || [];
    if (this._layerCount !== undefined && overlays.length > this._layerCount) {
      this._groupOpen = true;
      window.dispatchEvent(new CustomEvent("timeline:layer-added"));
    }
    this._layerCount = overlays.length;

    // Une animation posée se voit sur sa barre : la piste se déplie, et la première fois une
    // bulle pointe la marque (après le rendu, pour qu'elle ait son ancre).
    const anims = countAnimMarks(overlays, this.presetsValue);
    const animAdded = this._animCount !== undefined && anims > this._animCount;
    if (animAdded) this._groupOpen = true;
    this._animCount = anims;

    this._renderLayers(computeLayersTrack(
      overlays, editor.selection, totalOutputDuration(editor.composition), this._groupOpen,
      editor.fabric?.stackOrder?.() || [], this.presetsValue,
    ));
    if (animAdded) window.dispatchEvent(new CustomEvent("timeline:animation-added"));
  }

  // Les lignes sont toujours rendues : plier ou déplier la piste est une transition CSS sur
  // data-layers-open (cf. _form) — ici, seulement l'attribut, le compte et les lignes.
  _renderLayers(descriptor) {
    const trackEl = this.trackLayersTarget;
    this.tracksAreaTarget.toggleAttribute("data-layers-open", Boolean(descriptor?.header.active));
    if (!descriptor) { trackEl.replaceChildren(); return; }

    const { header, bars } = descriptor;
    this.layersSummaryTarget.textContent = header.summaryText;

    if (bars.length === 0) {
      trackEl.replaceChildren(this._cloneTpl("layersEmptyTpl"));
      return;
    }

    const frag = document.createDocumentFragment();
    for (const desc of bars) frag.appendChild(this._buildOverlayRow(desc));
    trackEl.replaceChildren(frag);
  }

  _buildOverlayRow(desc) {
    const row = this._cloneTpl("overlayTpl");
    row.querySelectorAll("[data-overlay-index]").forEach((el) => { el.dataset.overlayIndex = desc.index; });
    this._setParams(row, { "overlay-index": desc.index });
    byRole(row, "bar").setAttribute("data-tooltip-text-value", desc.tip);
    row.querySelectorAll('[data-role="anim-mark"]').forEach((el) => {
      const mark = desc.marks.find((m) => m.side === el.dataset.side);
      if (mark) el.setAttribute("data-tooltip-text-value", animMarkTip(mark));
    });
    row.dataset.overlayRow = desc.index;
    this._setValues(row, "composition-editor--tracks--overlay-bar", {
      left: pct(desc.left),
      width: pct(desc.width),
      selected: desc.selected || false,
      kind: desc.kind,
      label: desc.label,
      hidden: desc.hidden || false,
      "eye-icon": desc.eyeIcon,
      nested: desc.nested || false,
      marks: JSON.stringify(desc.marks),
    });
    return row;
  }

  _renderAudio(descriptor) {
    const track = this.trackAudioTarget;
    if (descriptor?.candidates && this.hasAudioCandidatesTplTarget) {
      // Un template : la bande nomme les candidats, un clic ouvre la page Audio.
      const el = this._cloneTpl("audioCandidatesTpl");
      el.querySelector('[data-role="name"]').textContent = descriptor.name;
      track.replaceChildren(el);
      return;
    }
    if (!descriptor) {
      // Sans musique : la ligne en pointillés qui mène à la page Audio (porteur seulement).
      const empty = this.hasAudioEmptyTplTarget && this.hasAudioUrlValue ? this._cloneTpl("audioEmptyTpl") : null;
      track.replaceChildren(...(empty ? [empty] : []));
      return;
    }

    const el = this._cloneTpl("audioTpl");
    this._setValues(el, "composition-editor--tracks--audio-bar", {
      left: pct(descriptor.left),
      width: pct(descriptor.width),
      selected: descriptor.selected || false,
      waveform: JSON.stringify(descriptor.waveformBars),
      name: descriptor.name,
    });
    track.replaceChildren(el);
  }

  // ==================== Ruler ====================

  onRulerPointerDown(e) {
    const move = (ev) => {
      const r = this.rulerTarget.getBoundingClientRect();
      const td = totalOutputDuration(this._editor.composition);
      this._editor.goTo(Math.max(0, Math.min(td, ((ev.clientX - r.left) / r.width) * td)));
      this._showScrubTc();
    };
    move(e);
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  }

  _showScrubTc() {
    this._editor.showScrubTc();
  }

  // ==================== Cut ====================

  // Couper la séquence sous la tête de lecture, après confirmation — un geste sans pointeur,
  // le même au doigt qu'à la souris (et au raccourci C).
  cutAtPlayhead() {
    const editor = this._editor;
    const time = editor.playTime;
    const segment = segmentAt(editor.composition, time);
    if (!segment || !isPlayableVideo(segment)) {
      editor.toast(t("composition_editor.cut.not_video", "Seule une séquence vidéo se coupe."));
      return;
    }
    const message = t("composition_editor.cut.confirm", "Couper la séquence à %{t} ?").replace("%{t}", formatTime(time));
    if (!window.confirm(message)) return;

    const before = capturePositions(".scene-clip");
    const index = cutAtTime(editor.composition, time);
    if (index === null) {
      editor.toast(t("composition_editor.cut.edge", "Impossible de couper au bord d'une séquence."));
      return;
    }
    editor.select("segment", index + 1);
    editor.mutate();
    playFlip(before, ".scene-clip");
    editor.toast(t("composition_editor.cut.done", "Séquence coupée"), true);
  }

  // ==================== Segment action handlers ====================

  onSegmentClick(e) {
    const idx = parseInt(e.currentTarget.dataset.segIndex);
    const seg = this._editor.composition.segments[idx];
    // Un plan à fournir : sélectionné, son fond vidéo ouvert dans la toolbox
    if (seg?.placeholder) {
      this._editor.openSegmentBackground(idx, "video");
      return;
    }
    // Le clic qui conclut un trim (pointerdown/up sur la poignée) n'est pas un clic.
    if (e.target.closest("[data-handle]")) return;
    // La tête de lecture va sous le clic, avant la sélection — qui ne la déplace que si
    // elle n'est pas déjà sur la séquence.
    this._editor.goTo(this._timeUnderPointer(e, idx));
    this._editor.select("segment", idx);
  }

  /** Le temps de sortie sous le pointeur, dans la séquence cliquée (bornée à sa dernière frame). */
  _timeUnderPointer(e, segIndex) {
    const composition = this._editor.composition;
    const seg = composition.segments[segIndex];
    const r = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const start = segmentOutputStart(composition, segIndex);
    const lastFrame = start + segmentOutputDuration(seg) - 1 / (composition.fps || 30);
    return Math.max(start, Math.min(lastFrame, start + segmentOutputDuration(seg) * frac));
  }

  onTrimStart(e) {
    this._startTrim(e, parseInt(e.params.segIndex), e.params.handle);
  }

  // Le « + » entre (ou après) les séquences : un plan à fournir, sélectionné
  insertSegment(e) {
    const idx = parseInt(e.currentTarget.dataset.insertIndex);
    this._editor.insertPlaceholderSegment(idx);
  }

  _deleteSegment(segIndex) {
    const composition = this._editor.composition;
    if (composition.segments.length <= 1) {
      this._editor.toast("Il faut au moins un segment");
      return;
    }
    const before = capturePositions(".scene-clip");
    removeSegment(composition, segIndex);
    this._editor.deselect();
    this._editor.mutate();
    playFlip(before, ".scene-clip");
    this._editor.toast("Segment supprimé", true);
  }

  _startTrim(e, segIndex, handle) {
    const editor = this._editor;
    startSegmentTrim(editor.composition, segIndex, handle, e, this._pxPerSec(), {
      onMove: (seg, ev) => {
        editor.tooltip.showDrag(segmentOutputDuration(seg).toFixed(1) + "s", {
          x: ev.clientX + 12,
          y: ev.clientY - 30,
        });
        this._renderMedia(
          computeMediaTrack(editor.composition.segments || [], editor.selection, this._pxPerSec()),
        );
        this._renderRuler(computeRuler(totalOutputDuration(editor.composition)));
      },
      onEnd: () => {
        editor.tooltip.hide();
        editor.mutate();
      },
    });
  }

  // ==================== Overlay action handlers ====================

  onToggleOverlayGroup() {
    this._groupOpen = !this._groupOpen;
    this._renderLayersTrack();
  }

  // Le nom du calque se clique comme sa barre : il sélectionne le bloc, sur la slide aussi.
  onOverlayClick(e) {
    if (e.target.closest('[data-role="eye"], [data-role="trash"]')) return;
    const idx = parseInt(e.currentTarget.dataset.overlayIndex);
    this._editor.select("overlay", idx);
    this._editor.fabric?.selectOverlay(idx);
  }

  onOverlayToggleEye(e) {
    const idx = parseInt(e.currentTarget.dataset.overlayIndex);
    toggleOverlayVisibility(this._editor.composition, idx);
    this._editor.mutate();
  }

  // La suppression passe par le contrôleur fabric : seul lui sait retirer l'objet du canvas
  // EN MÊME TEMPS que ses overlays. Pas de confirmation — comme pour un segment, l'undo est
  // le filet (et l'œil reste là pour un masquage réversible).
  onOverlayDelete(e) {
    const idx = parseInt(e.currentTarget.dataset.overlayIndex);
    if (!this._editor.composition.overlays?.[idx]) return;

    this._editor.fabric?.deleteOverlayAt(idx);
    this._editor.toast("Habillage supprimé", true);
  }

  // Une marque d'animation se clique, elle ne se tire pas : le geste ne part pas sur la barre.
  stopDrag(e) {
    e.stopPropagation();
  }

  // La marque ouvre le panneau Animations de ce calque (sélectionné sur la slide aussi).
  onAnimMarkClick(e) {
    const idx = parseInt(e.currentTarget.dataset.overlayIndex);
    this._editor.select("overlay", idx);
    this._editor.fabric?.openOverlayPage(idx, "animations");
  }

  onOverlayPointerDown(e) {
    if (e.target.closest('[data-role="eye"], [data-role="trash"]')) return;
    this._startOverlayDrag(e, parseInt(e.currentTarget.dataset.overlayIndex), null);
  }

  onOverlayHandleDown(e) {
    this._startOverlayDrag(e, parseInt(e.params.overlayIndex), e.params.handle);
  }

  _startOverlayDrag(e, idx, h) {
    const editor = this._editor;
    const overlay = editor.composition.overlays[idx];
    const td = totalOutputDuration(editor.composition);
    startOverlayDrag(editor.composition, overlay, h, e, this._pxPerSec(), td, {
      // Pendant le geste, les secondes au-dessus de la poignée tirée (de la barre si on la
      // glisse) — l'infobulle de glisser : la liste des calques défile, elle rognerait une
      // étiquette posée dedans.
      // La ligne vient d'être reconstruite : sa barre ne prend sa place qu'à la connexion de
      // son contrôleur — on la mesure à l'image suivante.
      onMove: () => {
        this._renderLayersTrack();
        cancelAnimationFrame(this._dragLabelFrame);
        this._dragLabelFrame = requestAnimationFrame(() => {
          const row = this.trackLayersTarget.querySelector(`[data-overlay-row="${idx}"]`);
          const anchor = row && (h ? byRole(row, `handle-${h.toLowerCase()}`) : byRole(row, "bar"));
          if (anchor) editor.tooltip.showDragAbove(dragLabel(overlay, h), anchor.getBoundingClientRect());
        });
      },
      onDragEnd: () => {
        cancelAnimationFrame(this._dragLabelFrame);
        editor.tooltip.hide();
        editor.mutate();
      },
      onClickEnd: () => {
        editor.select("overlay", idx);
        editor.fabric?.selectOverlay(idx);
      },
    });
  }

  // ==================== Audio action handlers ====================

  onAudioClick() {
    this._editor.select("audio", 0);
    this.openAudioPage();
  }

  onAudioPointerDown(e) {
    this._startAudioDrag(e, null);
  }

  onAudioHandleDown(e) {
    this._startAudioDrag(e, e.params.handle);
  }

  _startAudioDrag(e, h) {
    const editor = this._editor;
    const audio = editor.composition.audio;
    if (!audio) return;
    const td = totalOutputDuration(editor.composition);
    startAudioDrag(audio, h, e, this._pxPerSec(), td, {
      onMove: () => {
        this._renderAudio(computeAudioTrack(editor.composition.audio, editor.selection, td));
      },
      onEnd: () => editor.mutate(),
    });
  }

  // ==================== Soundtrack ====================

  // ==================== La musique ====================
  // La page Audio de la toolbox (`toolbox:show`) : depuis la ligne vide, la barre ou le ♫.
  openAudioPage() {
    if (!this.hasAudioUrlValue || !this.audioUrlValue) return;
    window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "audio", url: this.audioUrlValue } }));
  }

  removeAudio() {
    this._editor.removeAudio();
  }

  stopPropagation() {}

  // ==================== Sequence actions ====================
  // Les gestes de la barre de la séquence (sa page, FabricControls), par l'orchestrateur.

  // Stade template : la séquence se répète selon les photos du sujet — ou cesse de se répéter.
  toggleSegmentRepeat(index) {
    const seg = this._editor.composition.segments[index];
    if (!seg) return;

    if (seg.repeat) {
      delete seg.repeat;
    } else {
      seg.repeat = { over: "media", range: [1, 8] };
    }
    this._editor.mutate();
  }

  duplicateSegment(index) {
    const editor = this._editor;
    const before = capturePositions(".scene-clip");
    duplicateSegment(editor.composition, index);
    editor.mutate();
    playFlip(before, ".scene-clip");
    editor.toast("Segment dupliqué", true);
  }

  setSegmentSpeed(index, speed) {
    const editor = this._editor;
    const before = capturePositions(".scene-clip");
    setSegmentSpeed(editor.composition, index, speed);
    editor.mutate();
    playFlip(before, ".scene-clip");
  }

  // ==================== SortableJS ====================

  _setupSortable() {
    if (this._sortable) this._sortable.destroy();

    this._sortable = new Sortable(this.trackMediaTarget, {
      animation: 150,
      filter: "[data-handle]",
      preventOnFilter: false,
      draggable: ".scene-clip",
      ghostClass: "opacity-30",
      onStart: () => {},
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        reorderSegments(this._editor.composition, evt.oldIndex, evt.newIndex);
        this._editor.select("segment", evt.newIndex);
        this._editor.mutate();
      },
    });
  }

  // ==================== Private ====================

  get _editor() {
    return this.hasCompositionEditorOutlet ? this.compositionEditorOutlet : null;
  }

  _pxPerSec() {
    return this.rulerTarget.clientWidth / (totalOutputDuration(this._editor.composition) || 1);
  }

  // ==================== Drop zone (toolbox → timeline) ====================

  /**
   * Determine whether the drop should insert or replace, and at which index.
   * If the cursor is near a segment boundary (within 12px), it's an insert.
   * Otherwise it's a replace on the segment under the cursor.
   */
  _dropTarget(e) {
    const track = this.trackMediaTarget;
    const wraps = [...track.querySelectorAll("[data-role='seg-wrap'][data-id]")];
    if (!wraps.length) return { kind: "insert", index: 0 };

    const mx = e.clientX;
    const INSERT_ZONE = 12; // px from edge to trigger insert

    for (let i = 0; i < wraps.length; i++) {
      const rect = wraps[i].getBoundingClientRect();
      if (mx < rect.left + INSERT_ZONE) {
        return { kind: "insert", index: i };
      }
      if (mx <= rect.right - INSERT_ZONE) {
        return { kind: "replace", index: i };
      }
      if (mx <= rect.right + INSERT_ZONE) {
        return { kind: "insert", index: i + 1 };
      }
    }
    // Past the last segment
    return { kind: "insert", index: wraps.length };
  }

  /**
   * Show a visual drop indicator on the timeline.
   * For insert: expand the "+" zone at the boundary.
   * For replace: add a ring on the target segment.
   */
  _showDropIndicator(kind, index) {
    this._clearDropIndicator();

    const track = this.trackMediaTarget;
    const wraps = [...track.querySelectorAll("[data-role='seg-wrap']")];

    if (kind === "insert") {
      // Find the insert zone at the right boundary
      const wrap = wraps[index] || wraps[wraps.length - 1];
      if (!wrap) return;
      const insertBtn = wrap.querySelector("[data-insert-index] button, button[data-insert-index]")
        || byRole(wrap, "insert")?.querySelector("button");
      if (insertBtn) {
        insertBtn.dataset.dropActive = "true";
        insertBtn.classList.add("!h-8", "!w-8", "!border-brand_main", "!bg-surface_0", "!text-brand_main", "!overflow-visible");
        this._activeDropEl = insertBtn;
      }
    } else {
      // Highlight the segment
      const wrap = wraps[index];
      if (!wrap) return;
      const content = byRole(wrap, "content");
      if (content) {
        content.dataset.dropTarget = "true";
        content.classList.add("ring-2", "ring-brand_main", "rounded-lg");
        this._activeDropEl = content;
      }
    }
  }

  _clearDropIndicator() {
    if (!this._activeDropEl) return;
    const el = this._activeDropEl;
    if (el.dataset.dropActive) {
      delete el.dataset.dropActive;
      el.classList.remove("!h-8", "!w-8", "!border-brand_main", "!bg-surface_0", "!text-brand_main", "!overflow-visible");
    }
    if (el.dataset.dropTarget) {
      delete el.dataset.dropTarget;
      el.classList.remove("ring-2", "ring-brand_main", "rounded-lg");
    }
    this._activeDropEl = null;
  }

}
